const KeenSlider = window.KeenSlider

const { setInitialised, useA11y, useArrows } = window.BAO.Carousel.plugins
const { debounce } = window.BAO.Utils

class PushCartCarousel extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.carousel = this.setupCarousel()
  }

  setupListeners () {
    this.listeners.add(
      window,
      'resize',
      debounce(() => {
        this.carousel?.destroy()
        this.carousel = this.setupCarousel()
      }, 500),
    )
  }

  // Note: aria-hidden and tabindex management for non-visible slides is
  // already handled (with correct multi-slide-visible overlap detection) by
  // the shared useA11y() carousel plugin. This method only layers on the
  // supplementary "(Selected)" screen-reader text for visible product links,
  // using the aria-hidden state useA11y has already set, so the two never
  // fight over which slides are focusable/announced.
  updateSlideAccessibility (carousel = this.carousel) {
    if (!this.els.slide?.exists) return

    const slides = this.els.slide?.elements || []

    slides.forEach((slide) => {
      if (!slide.hasAttribute('aria-hidden')) return

      const isVisible = slide.getAttribute('aria-hidden') !== 'true'

      slide.setAttribute('aria-current', String(isVisible))

      // Keep selected state explicit for screen readers on visible product-type links.
      const productTypeLinks = slide.querySelectorAll(
        'a[href]:not([aria-hidden="true"])',
      )

      productTypeLinks.forEach((link) => {
        link.setAttribute('aria-current', String(isVisible))

        let selectedText = link.querySelector(
          '[data-push-cart-carousel-el="selected-text"]',
        )

        if (!selectedText) {
          selectedText = document.createElement('span')
          selectedText.className = 'util-ScreenReaderOnly'
          selectedText.setAttribute(
            'data-push-cart-carousel-el',
            'selected-text',
          )
          link.appendChild(selectedText)
        }

        selectedText.textContent = isVisible ? ' (Selected)' : ''
      })
    })
  }

  // Called when this breakpoint has no KeenSlider carousel (plain scrollable
  // list). Clears any aria-hidden/tabindex="-1"/"(Selected)" state a
  // previously-destroyed carousel instance may have left on slides, since
  // every slide in a scrollable list is reachable and none should be hidden.
  resetSlideAccessibility () {
    if (!this.els.slide?.exists) return

    const slides = this.els.slide?.elements || []

    slides.forEach((slide) => {
      slide.removeAttribute('aria-hidden')
      slide.removeAttribute('aria-current')

      slide
        .querySelectorAll('[data-carousel-original-tabindex]')
        .forEach((element) => {
          const originalTabindex = element.getAttribute(
            'data-carousel-original-tabindex',
          )

          if (originalTabindex) {
            element.setAttribute('tabindex', originalTabindex)
          } else {
            element.removeAttribute('tabindex')
          }

          element.removeAttribute('data-carousel-original-tabindex')
        })

      const selectedText = slide.querySelector(
        '[data-push-cart-carousel-el="selected-text"]',
      )

      if (selectedText) {
        selectedText.textContent = ''
      }

      slide
        .querySelectorAll('a[href]:not([aria-hidden="true"])')
        .forEach((link) => link.removeAttribute('aria-current'))
    })
  }

  // A destroyed carousel can leave a native disabled attribute behind, which would block vertical-mode clicks.
  resetControlState () {
    const controls = [this.els?.previous?.element, this.els?.next?.element]

    controls.forEach((control) => {
      control?.removeAttribute('disabled')
      control?.removeAttribute('aria-disabled')
    })
  }

  hasExposedControls () {
    const controls = this.els?.previous?.element?.closest(
      '.psh-Recommendations_Controls',
    )

    return (
      Boolean(controls) && window.getComputedStyle(controls).display !== 'none'
    )
  }

  setupCarousel () {
    if (!this.els.slide?.exists) return

    this.resetSlideAccessibility()
    this.resetControlState()

    if (window.BAO.MediaBreakpoints.isSm) {
      // Hiding off-screen cards is only safe when Previous/Next can reach them.
      return this.hasExposedControls()
        ? this.setupVerticalCarousel()
        : undefined
    }

    const carousel = new KeenSlider(
      this.els.slides.element,
      {
        selector: this.els.slide.selector,
        defaultAnimation: {
          duration: 1500,
        },
        loop: false,
        mode: 'snap',
        slides: {
          perView: 2.1,
          spacing: 14,
        },
      },
      [
        setInitialised(),
        useA11y({ manageSlideFocus: this.hasExposedControls() }),
        useArrows({
          next: this.els?.next?.element,
          previous: this.els?.previous?.element,
          keepFocusableAtEdges: true,
        }),
      ],
    )

    carousel.on('slideChanged', () => {
      this.updateSlideAccessibility(carousel)
    })

    this.updateSlideAccessibility(carousel)

    return carousel
  }

  setupVerticalCarousel () {
    const container =
      this.closest('.drw-Drawer_AsideInner') ||
      this.querySelector('.psh-Recommendations_Carousel')
    const previous = this.els?.previous?.element
    const next = this.els?.next?.element
    const selector =
      'a[href], button, input, select, textarea, summary, iframe, audio[controls], video[controls], [contenteditable], [tabindex]'

    const update = () => {
      const bounds = container.getBoundingClientRect()
      if (!bounds.width || !bounds.height) return
      const header = this.querySelector('.psh-Recommendations_Header')
      const visibleTop = Math.max(
        bounds.top,
        header?.getBoundingClientRect().bottom || bounds.top,
      )

      this.els.slide.elements.forEach((slide) => {
        const rect = slide.getBoundingClientRect()
        const hidden = rect.bottom <= visibleTop || rect.top >= bounds.bottom
        slide.setAttribute('aria-hidden', String(hidden))

        slide.querySelectorAll(selector).forEach((element) => {
          const attribute = 'data-carousel-original-tabindex'
          if (hidden) {
            if (!element.hasAttribute(attribute)) {
              element.setAttribute(
                attribute,
                element.getAttribute('tabindex') ?? '',
              )
            }
            element.setAttribute('tabindex', '-1')
          } else if (element.hasAttribute(attribute)) {
            const original = element.getAttribute(attribute)
            if (original === '') element.removeAttribute('tabindex')
            else element.setAttribute('tabindex', original)
            element.removeAttribute(attribute)
          }
        })
      })

      const isAtStart = container.scrollTop <= 1
      const isAtEnd =
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 1

      // aria-disabled (not disabled) keeps a focused arrow from losing focus at either end.
      previous?.setAttribute('aria-disabled', String(isAtStart))
      next?.setAttribute('aria-disabled', String(isAtEnd))
      previous?.removeAttribute('aria-hidden')
      next?.removeAttribute('aria-hidden')
      this.updateSlideAccessibility()
    }

    const scrollPrevious = () => {
      if (previous.getAttribute('aria-disabled') === 'true') return

      container.scrollBy({ top: -container.clientHeight, behavior: 'smooth' })
    }
    const scrollNext = () => {
      if (next.getAttribute('aria-disabled') === 'true') return

      container.scrollBy({ top: container.clientHeight, behavior: 'smooth' })
    }
    container.addEventListener('scroll', update)
    previous?.addEventListener('click', scrollPrevious)
    next?.addEventListener('click', scrollNext)
    const observer = new ResizeObserver(update)
    observer.observe(container)
    this.els.slide.elements.forEach((slide) => observer.observe(slide))
    update()

    return {
      update,
      destroy: () => {
        observer.disconnect()
        container.removeEventListener('scroll', update)
        previous?.removeEventListener('click', scrollPrevious)
        next?.removeEventListener('click', scrollNext)
      },
    }
  }
}

if (!customElements.get('push-cart-carousel')) {
  customElements.define('push-cart-carousel', PushCartCarousel)
}
